import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppSelector } from '../store/hooks';
import axios from 'axios';
import LoadingSpinner from './LoadingSpinner';
import { formatDateEuropean } from '../utils/dateUtils';
import { getApiErrorMessage } from '../utils/apiError';
import './Students.css';
import './GroupForm.css';
import './ExpiringSubscriptions.css';

const API_BASE_URL = import.meta.env.VITE_API_URL as string;

type ExpiryStatus = 'expired' | 'critical' | 'warning';

interface ExpiringRow {
  studentId: number;
  studentName: string;
  contact: string | null;
  teacherId: number;
  groupId: number;
  groupName: string;
  subscriptionId: number;
  typeName: string;
  lessonsCount: number;
  remaining: number;
  lastLessonDate: string | null;
  status: ExpiryStatus;
}

interface ExpiringMeta {
  total: number;
  critical: number;
}

// A row is "critical" (drives Telegram / dashboard widget / this filter) when ≤1 lesson remains.
const isCritical = (row: ExpiringRow) => row.remaining <= 1;

const remainingLabel = (remaining: number) => {
  if (remaining <= 0) return 'Expired';
  if (remaining === 1) return '1 lesson left';
  return `${remaining} lessons left`;
};

const ExpiringSubscriptions: React.FC = () => {
  const navigate = useNavigate();
  const token = useAppSelector((state) => state.auth?.token);
  const [searchParams, setSearchParams] = useSearchParams();

  const [rows, setRows] = useState<ExpiringRow[]>([]);
  const [meta, setMeta] = useState<ExpiringMeta>({ total: 0, critical: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Toggle mirrors the ?filter=critical URL param so the widget / Telegram deep link lands filtered.
  const criticalOnly = searchParams.get('filter') === 'critical';

  const setCriticalOnly = (value: boolean) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set('filter', 'critical');
    else next.delete('filter');
    setSearchParams(next, { replace: true });
  };

  const fetchExpiring = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_BASE_URL}/subscriptions/expiring`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setRows(response.data.subscriptions ?? []);
      setMeta(response.data.meta ?? { total: 0, critical: 0 });
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to fetch expiring subscriptions'));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void Promise.resolve().then(() => fetchExpiring());
  }, [fetchExpiring]);

  // Fetch the full window once; filter client-side so both counts stay available.
  const visibleRows = criticalOnly ? rows.filter(isCritical) : rows;

  return (
    <div className="students-container">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Expiring subscriptions</h1>
          <p>Students running low on lessons — contact them about renewal</p>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)} className="close-error">×</button>
        </div>
      )}

      {/* Filters */}
      <div className="filter-bar">
        <div className="risk-seg" role="group" aria-label="Expiry filter">
          <button
            type="button"
            className={`risk-seg__btn ${!criticalOnly ? 'risk-seg__btn--active' : ''}`}
            onClick={() => setCriticalOnly(false)}
          >
            All expiring ({meta.total})
          </button>
          <button
            type="button"
            className={`risk-seg__btn ${criticalOnly ? 'risk-seg__btn--active' : ''}`}
            onClick={() => setCriticalOnly(true)}
          >
            Critical ({meta.critical})
          </button>
        </div>
      </div>

      {/* Results Summary */}
      <div className="results-summary">
        <p>
          Showing {visibleRows.length} {criticalOnly ? 'critical' : 'expiring'} subscription{visibleRows.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Table */}
      <div className="students-table-container">
        {loading ? (
          <LoadingSpinner />
        ) : visibleRows.length === 0 ? (
          <div className="no-students">
            <p>{criticalOnly ? 'No critical subscriptions.' : 'No expiring subscriptions.'}</p>
          </div>
        ) : (
          <table className="students-table">
            <thead>
              <tr>
                <th>Student</th>
                <th>Group</th>
                <th>Type</th>
                <th>Status</th>
                <th>Last lesson</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row) => (
                <tr key={row.subscriptionId}>
                  <td><strong>{row.studentName}</strong></td>
                  <td>{row.groupName}</td>
                  <td>{row.typeName}</td>
                  <td>
                    <span className={`exp-badge exp-badge--${row.status}`}>
                      {remainingLabel(row.remaining)}
                    </span>
                  </td>
                  <td>{row.lastLessonDate ? formatDateEuropean(row.lastLessonDate) : '—'}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        type="button"
                        className="btn-icon"
                        data-tooltip="Edit subscription"
                        onClick={() => navigate(`/subscriptions/edit/${row.studentId}/${row.subscriptionId}`)}
                      >
                        ✎
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default ExpiringSubscriptions;
